/** Public marketing pages. They load whatever they can from the open API and still render if it is down. */
import { api } from '../../core/api.js';
import { config } from '../../core/config.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { inr } from '../../core/format.js';
import { $ } from '../../core/dom.js';
import { every, update } from '../../app/shell.js';
import { av, banner, chips, ic, ring } from '../../ui/components.js';
import { CERTIFICATE_NOTE, courseCards, courseDetail } from '../courses/views.js';
import { classShot, ctaBand, finalCta, pathWidget, pubHead, sceneCard, STOPS, stepsBlock, trainerStrip, trustStrip, why } from './blocks.js';

const quiet = (promise) => promise.catch(() => null);
const open = { auth: false };

async function loadInfo() {
  const data = await quiet(api.get('/public/info', open));
  return {
    stats: data?.stats ?? null,
    settings: data?.settings ?? { deposit_paise: 50000, deposit_hold_days: 7, gst_percent: 18 },
  };
}

/* ---------- home ---------- */

function movePath(ui, index, manual) {
  ui.pathIndex = index;
  if (manual) ui.pathPause = Date.now() + 5000;
}

function paintFill() {
  const fill = $('.stops .fill');
  const stops = document.querySelectorAll('.stop');
  const current = $('.stop.on');
  if (fill && stops.length && current) fill.style.height = `${current.offsetTop - stops[0].offsetTop}px`;
}

register({
  'home.stop': (el) => {
    update((ui) => movePath(ui, Number(el.dataset.index), true));
    paintFill();
  },
});

const home = {
  path: '/',
  title: '',
  ui: () => ({ pathIndex: 0, pathPause: 0 }),
  async load() {
    const [courses, trainers, info] = await Promise.all([
      quiet(api.get('/courses', { ...open, query: { limit: 3 } })),
      quiet(api.get('/public/trainers', open)),
      loadInfo(),
    ]);
    return { courses: courses?.courses ?? [], trainers: trainers?.trainers ?? [], ...info };
  },
  enter() {
    paintFill();
    every(1800, () => {
      update((ui) => {
        if (Date.now() >= ui.pathPause) movePath(ui, (ui.pathIndex + 1) % STOPS.length, false);
      });
      paintFill();
    });
  },
  view(d, ui) {
    const brand = config.brandName;
    const scenes = [
      {
        side: 'left', pills: ['Live batches', 'Weekly tests'], title: 'Students / Job seekers', sub: 'Learn from people who do the job today',
        text: 'Studying, a fresher or already experienced: live classes, weekly tests and real projects, then opportunities come to you once your skills are proven.',
        panel: 'Tonight · Manual Testing #04',
        rows: Object.assign([
          ['Class 13 · API testing with Postman', 'Live with your trainer and batch', '7:00 PM', 'gold'],
          ['Matched: Junior QA Tester', 'Recommended by Vignova', 'Shortlisted', 'ok'],
        ]),
        ticks: ['Live classes', 'Projects and assessments', 'Certificates', 'Opportunities come to you'], href: '/students', cta: 'For students and job seekers',
      },
      {
        side: 'right', pills: ['Teach', 'Freelance'], title: 'Working professionals', sub: 'Share your experience, earn on the side',
        text: 'Teach live batches and take freelance work that matches your skills. We handle the classroom, scheduling and payments.',
        panel: 'Your batch · Manual Testing #04',
        rows: [
          ['Mon, Wed, Fri at 7:00 PM', '12 students in the batch', 'Next: Class 13', 'gold'],
          ['Requirement match', 'API automation project', 'New', 'gold'],
        ],
        ticks: ['Paid per class taught', 'Classroom handled', 'Freelance requirements', 'No random messages'], href: '/freelancers', cta: 'For professionals',
      },
      {
        side: 'left', pills: ['Build', 'Co-founders'], title: 'Startups', sub: 'Find the people to build with',
        text: 'Developers, designers, testers and co-founders, introduced by Vignova instead of a public job post.',
        panel: 'Launch team · 3 open roles',
        rows: [
          ['Frontend developer', 'React · tested on a real project', '3 matched', 'gold'],
          ['Co-founder · Product', 'Introduced by Vignova', 'Intro sent', 'ok'],
        ],
        ticks: ['Tested people', 'No public job post', 'Controlled introductions', 'Co-founder matching'], href: '/startups', cta: 'For startups',
      },
      {
        side: 'right', pills: ['Matched', 'Verified'], title: 'Companies', sub: 'Prepared talent, not a crowd',
        text: 'Tell Vignova what you need and get a short list of people taught and tested every week.',
        panel: 'Requirement · Junior QA Tester',
        rows: Object.assign([
          ['Example candidate', 'Manual Testing · API Testing · Jira', '91% match', 'gold'],
          ['Chat', 'Opens once you shortlist', 'Locked', ''],
        ]),
        ticks: ['A short list, not CVs', 'Evidence behind every match', 'Controlled contact', 'Shape the pipeline'], href: '/companies', cta: 'For companies',
      },
    ];
    return html`<section class="cine-hero">
        <span class="eyebrow">${brand} by Vignova</span>
        <h1>Where skills turn into <em>opportunities</em>.</h1>
        <p class="lede">${brand} brings students, working professionals, companies and startups together through live industry-led learning, verified skills and matched opportunities.</p>
        <div class="row"><a class="btn lg" href="/join">Join ${brand}</a><a class="btn ghost lg" href="#scenes">See how it works</a></div>
        <div class="cine-chips">${['Connect', 'Learn', 'Build', 'Grow'].map((w) => html`<span>${w}</span>`)}</div>
        <div class="cue"><i></i>Scroll to explore</div>
      </section>
      <div class="scenes" id="scenes">${[0, 2].map((at) => html`<div class="scene-pair">${scenes.slice(at, at + 2).map((sc, i) => sceneCard({ ...sc, index: at + i + 1, total: scenes.length }))}</div>`)}</div>
      ${trustStrip(d.stats)}
      <section class="cine-block journey" id="aud"><div><span class="eyebrow">The path</span><h2>One path, from first class to <em>first offer</em>.</h2>
        <p class="mu" style="font-size:17px;max-width:460px">Learn, build, verify, match, connect, work, and come back to teach the next batch. Every step happens inside ${brand}.</p>
        <a class="btn ghost mt" href="/about">How matching works</a></div>${pathWidget(ui.pathIndex)}</section>
      <section class="cine-block"><div class="sec-h"><span class="eyebrow">Why ${brand}</span><h2>Built different, <em>on purpose</em>.</h2></div>
        <div class="bento">
          <div class="card big"><span class="n">01</span><h3>No open job board.</h3><p>Opportunities appear inside your account, matched to skills you have proven. Nobody scrolls past you in a crowd.</p></div>
          <div class="card"><span class="n">02</span><h3>No random messages</h3><p>Chats open only through a shared batch, an accepted connection or a shortlist.</p></div>
          <div class="card"><span class="n">03</span><h3>Verified skills</h3><p>Courses, projects and weekly assessments sit behind every profile.</p></div>
          <div class="card"><span class="n">04</span><h3>Reviewed by people</h3><p>Vignova checks every requirement and recommendation by hand.</p></div>
          <div class="card"><span class="n">05</span><h3>Month to month</h3><p>Pay for courses monthly and stop any time. No loan, no long contract.</p></div>
          <div class="card"><span class="n">06</span><h3>Taught by practitioners</h3><p>Every trainer is a working professional, checked before they teach.</p></div>
        </div></section>
      ${classShot()}
      ${d.courses.length ? html`<section class="cine-block"><div class="row between sec-h" style="max-width:none"><div><span class="eyebrow">Courses</span><h2 style="margin:0">Popular <em>right now</em>.</h2></div><a class="btn ghost sm" href="/courses">See all courses</a></div>${courseCards(d.courses)}</section>` : ''}
      ${trainerStrip(d.trainers)}
      <section class="cine-block faq"><div><span class="eyebrow">FAQ</span><h2>Questions</h2><p class="mu">Quick answers about how ${brand} works. Something else on your mind? <a href="/contact">Write to us</a>.</p></div><div class="faq-list">
        <details><summary>Can companies see every student?</summary><p>No. Companies only see profiles that Vignova recommends for one of their requirements.</p></details>
        <details><summary>Is the freelancer deposit a fee?</summary><p>No. It is a refundable ${inr(d.settings.deposit_paise)} deposit, held for ${d.settings.deposit_hold_days} days, that keeps out spam and fake profiles. It does not guarantee projects, students or earnings.</p></details>
        <details><summary>Can anyone message me?</summary><p>No. Chats open only through a shared batch, an accepted connection, an approved project or a shortlist.</p></details>
        <details><summary>How are courses paid for?</summary><p>Month to month${config.gstRegistered ? ', with GST' : ''}. You can stop any time; there is no long-term contract or loan.</p></details></div></section>
      ${finalCta('Ready to start?', 'Pick a course, join a live batch this week, and let your skills do the talking.', 'any')}`;
  },
};

/* ---------- audience pages ---------- */

const students = {
  path: '/students',
  title: 'Students / Job seekers',
  async load() {
    const [courses, trainers] = await Promise.all([quiet(api.get('/courses', open)), quiet(api.get('/public/trainers', open))]);
    return { courses: courses?.courses ?? [], trainers: trainers?.trainers ?? [] };
  },
  view: (d) => html`${pubHead('Learn from people who do the work', 'For students and job seekers, fresher or experienced. Live batches taught by working professionals, with projects, weekly assessments and certificates.')}
    ${d.courses.length ? courseCards(d.courses) : ''}${trainerStrip(d.trainers)}
    ${why('Why students and job seekers join', [
      ['Already have experience?', 'Join as a job seeker. Add your experience, build the skills employers ask for, and get matched to real roles.'],
      ['Taught by practitioners', 'A working QA engineer or developer teaches the tools and habits the job needs.'],
      ['Low risk', 'Pay month to month and leave any time. No big upfront fee or loan.'],
      ['Weekly feedback', 'Weekly tests show where you stand, so you know you are improving.'],
      ['Real proof', 'Projects, assessments and certificates give you something to show.'],
      ['Opportunities come to you', 'Consistent learners get recommended to companies by Vignova.'],
      ['No spam', 'Only your trainer, connections and shortlisting companies can message you.'],
    ])}
    ${stepsBlock('How it works', [['Choose a course', 'Pick a skill and a trainer.'], ['Join a live batch', 'Attend classes and use the batch chat.'], ['Build and verify', 'Finish projects and assessments.'], ['Get matched', 'Receive opportunities inside your account.']])}
    ${ctaBand('Start learning this week', 'student')}`,
};

const freelancers = {
  path: '/freelancers',
  title: 'Freelancers',
  load: loadInfo,
  view: (d) => html`${pubHead('Share your experience. Earn on the side.', 'Teach live batches and take freelance requirements that match your skills.')}
    <div class="card tint"><h3 style="margin:0">Teach what you already know</h3><p class="mu" style="margin:6px 0 0">Earnings depend on your skills, experience, availability and the requirements on offer. Nothing is guaranteed.</p></div>
    <div class="grid g2 mt"><div class="card flat"><h3>Refundable verification deposit</h3><div class="stat"><div class="k">${inr(d.settings.deposit_paise)}</div></div><p class="mu">Held for ${d.settings.deposit_hold_days} days. It keeps out spam and fake profiles and is refunded if there are no policy violations. It does not guarantee projects, students or earnings.</p></div>
      <div class="card flat"><h3>What you can do</h3>${chips(['Teach batches', 'Freelance projects', 'Startup work', 'Mentoring'])}<p class="mu mt-s">Batches, classroom, scheduling and payment records are handled for you.</p></div></div>
    ${why('Why professionals join', [
      ['Extra income from skills you already have', 'Paid per class taught, with the rate set per batch.'],
      ['Students and logistics provided', 'We handle batches, the classroom, scheduling and payouts.'],
      ['Safe communication', 'Students cannot message you at random.'],
      ['More work options', 'Take freelance requirements alongside teaching.'],
      ['A serious network', 'The refundable deposit keeps out low-effort sign-ups.'],
      ['Build your name', 'Strong trainers grow into larger batches and better rates.'],
    ])}
    ${stepsBlock('How it works', [['Register', 'Add your role, skills and rates.'], ['Verify', 'Pay the refundable deposit.'], ['Teach or apply', 'Accept a batch or apply to requirements.'], ['Get paid', 'Track earnings and withdraw.']])}
    ${ctaBand('Bring your skills to VConnect', 'professional')}`,
};

const companies = {
  path: '/companies',
  title: 'Companies',
  view: () => html`${pubHead('Find prepared talent, not a crowd', 'Tell Vignova what you need. We recommend a short list of verified, tested people.')}
    <div class="card flat"><h3>How contact works</h3><div class="steps mt-s">${['Post a requirement', 'Vignova matches', 'You review profiles', 'You shortlist', 'Chat unlocks'].map((x) => html`<div class="card flat" style="padding:44px 14px 14px"><b class="sm">${x}</b></div>`)}</div><p class="note mt-s">Companies cannot browse or message the full student database.</p></div>
    <section class="sec"><div class="sec-h"><h2>What a recommended profile looks like</h2><p>An illustration, not a real person.</p></div>
      <div class="card" style="max-width:460px"><div class="row nw">${av('Example Candidate', 'lg')}<div class="f1"><h3 style="margin:0">Example candidate</h3><p class="mu sm" style="margin:0">College name, City</p></div>${ring(91)}</div>
        <div class="mt-s">${chips(['Manual Testing', 'API Testing', 'Jira'])}</div><p class="sm mt-s" style="margin-bottom:0">Assessment average, projects completed and profile completeness sit behind every match.</p></div></section>
    ${why('Why companies join', [
      ['A short list, not a pile of CVs', 'Vignova shares only people who were taught and tested weekly.'],
      ['Evidence behind every match', 'Scores, projects and trainer feedback sit behind each recommendation.'],
      ['Trained on current tools', 'Candidates learn from people working in teams today.'],
      ['Controlled contact', 'You shortlist, chat unlocks, and everyone knows why they are talking.'],
      ['Shape the pipeline', 'Ask for skills and Vignova prepares people for them.'],
    ])}
    ${ctaBand('Post your first requirement', 'company')}`,
};

const startups = {
  path: '/startups',
  title: 'Startups',
  view: () => html`${pubHead('Find the people to build with', 'Developers, designers, testers, marketers, product people and co-founders.')}
    ${chips(['Developers', 'Testers', 'Designers', 'Marketing', 'AI professionals', 'Product', 'Co-founders'])}
    ${why('Why startups join', [
      ['Tested people', 'Everyone recommended has passed practical assessments.'],
      ['No public job post', 'Skip the flood of replies; Vignova introduces suitable people.'],
      ['Controlled introductions', 'Chat opens only once you shortlist or connect.'],
    ])}
    ${stepsBlock('How it works', [['Post a requirement', 'Role, skills, budget, timeline.'], ['Vignova matches', 'Suitable people are recommended.'], ['Connect', 'Chat opens with the people you choose.']])}
    ${ctaBand('Build your team', 'startup')}`,
};

const about = {
  path: '/about',
  title: 'About',
  view: () => html`${pubHead('About VConnect', `A controlled professional ecosystem by ${config.companyName}.`)}
    <div class="card flat"><div class="chips">${STOPS.map((s) => html`<span class="tag">${s[0]}</span>`)}</div>
      <p class="mt-s" style="margin:12px 0 0;max-width:700px">VConnect is where people learn from working professionals, prove what they can do, and get matched to real work. Vignova reviews every requirement and every recommendation by hand before it reaches anyone.</p></div>
    ${why('What we believe', [['Not a social network', 'Every conversation has a reason and a context.'], ['Skills over claims', 'Courses, projects and assessments verify profiles.'], ['Fair for everyone', 'Students learn, professionals earn, companies hire with confidence.']])}
    <section class="sec"><div class="sec-h"><h2>How the matching score works</h2><p>The Vignova Matching Score combines skill overlap with the requirement (60%) and assessment results (40%). Vignova staff review the list before it is shared.</p></div></section>
    ${ctaBand('Be part of it', 'student')}`,
};

/* ---------- courses ---------- */

const courses = {
  path: '/courses',
  title: 'Courses',
  ui: (ctx) => ({ category: ctx.query.get('category') || 'All' }),
  async load() {
    const [list, cats] = await Promise.all([api.get('/courses', { ...open, query: { limit: 50 } }), quiet(api.get('/courses/categories', open))]);
    return { courses: list.courses, categories: cats?.categories ?? [] };
  },
  view(d, ui) {
    const shown = ui.category === 'All' ? d.courses : d.courses.filter((c) => c.category === ui.category);
    return html`${pubHead('All courses', 'Every course is a live batch with a working professional.')}
      <div class="mb">${banner(CERTIFICATE_NOTE, '', 'award')}</div>
      ${d.categories.length > 1 ? html`<div class="mb"><div class="seg" role="tablist" aria-label="Category">${['All', ...d.categories].map((c) => html`<button type="button" role="tab" aria-selected="${c === ui.category}" class="${c === ui.category ? 'on' : ''}" data-act="courses.category" data-value="${c}">${c}</button>`)}</div></div>` : ''}
      ${shown.length ? courseCards(shown) : html`<div class="card empty">${ic('book')}<h3>No courses here yet</h3><p>New batches are added regularly.</p></div>`}
      ${ctaBand('Found one you like?', 'student')}`;
  },
};

register({ 'courses.category': (el) => update((ui) => { ui.category = el.dataset.value; }) });

const courseDetailPage = {
  path: '/course/:slug',
  title: (d) => d?.course?.title || 'Course',
  async load(ctx) {
    return api.get(`/courses/${encodeURIComponent(ctx.params.slug)}`, { ...open, signal: ctx.signal });
  },
  view: (d, _ui, ctx) => html`<div style="padding-top:28px"><a class="back" href="/courses">${ic('left')} All courses</a></div>
    ${courseDetail({
      course: d.course, trainer: d.trainer, batches: d.batches, gst: d.gst_percent ?? 0, trial: d.trial,
      action: html`<a class="btn lg wide" href="/register/student?next=${encodeURIComponent(`/student/course/${d.course.slug}`)}">Join to enrol</a><p class="note mt-s">Already have an account? <a href="/login?next=${encodeURIComponent(`/student/course/${ctx.params.slug}`)}">Log in</a></p>`,
    })}`,
};

/* ---------- 404 ---------- */

const notFound = {
  path: '/404',
  title: 'Page not found',
  view: () => html`<div class="sec">${banner('This link does not exist. It may have moved.', '', 'alert')}<a class="btn mt" href="/">Go home</a></div>`,
};

export const routes = [home, students, freelancers, companies, startups, about, courses, courseDetailPage, notFound];
